import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { PROJECT_FIELDS_PAGE_VIEWED } from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadProjectFieldsPageViewed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().projectFields.pageViewed === 'boolean') {
			return;
		}

		try {
			const pageViewed = await getUserProperty<boolean>(accountId, PROJECT_FIELDS_PAGE_VIEWED);

			const state = getState();

			setState({
				...state,
				projectFields: {
					...state.projectFields,
					pageViewed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					projectFields: {
						...state.projectFields,
						pageViewed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.project-fields.page-viewed',
				`Failed to get user property with key: ${PROJECT_FIELDS_PAGE_VIEWED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const setProjectFieldsPageViewed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			projectFields: {
				...state.projectFields,
				pageViewed: true,
			},
		});

		try {
			await setUserProperties(accountId, PROJECT_FIELDS_PAGE_VIEWED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.project-fields.page-viewed',
				`Failed to set user property with key: ${PROJECT_FIELDS_PAGE_VIEWED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};
